import {
  type ObjectKindId,
  ObjectKindType,
  ObjectKindTypeId,
  type ObjectRoomId,
} from '@previo/codebooks'
import { DateTimeRange } from '@previo/design-system'
import { ObjectRoom } from '~/store/property/objectKinds/model/ObjectRoom'
import { CallableOnce } from '~/decorators/CallableOnce'

export class ObjectKind {
  private _objectRooms: ObjectRoom[] = []

  constructor(
    public readonly objectKindId: ObjectKindId,
    public readonly name: string,
    public readonly objectKindTypeId: ObjectKindTypeId,
    public readonly numberOfBeds: number,
    public readonly numberOfExtraBeds: number,
    public readonly maxWithoutBeds: number,
    public readonly slotInterval: null | number = 30,
    public readonly openingHours: DateTimeRange[][],
  ) {}

  totalCapacity(): number {
    return this.numberOfBeds + this.numberOfExtraBeds
  }

  hasCapacity(): boolean {
    return this.totalCapacity() > 0 && !this.isOtherDays()
  }

  capacityString(): string {
    return !this.isOtherDays() ? `${this.numberOfBeds} + ${this.numberOfExtraBeds}` : ''
  }

  isOtherDays(): boolean {
    return this.objectKindTypeId === ObjectKindTypeId.OTHER_DAYS
  }

  isHourlyBased(): boolean {
    return ObjectKindType.getById(this.objectKindTypeId)!.isHourlyBased
  }

  hasOpeningHours(): boolean {
    return this.openingHours.some((intervals) => intervals.length > 0)
  }

  getOpeningHoursForDay(dayIndex: number): DateTimeRange[] {
    return this.openingHours[dayIndex] ?? []
  }

  get hasAnyObjectRooms(): boolean {
    return this._objectRooms.length > 0
  }

  get withCapacity(): string {
    if (!this.hasCapacity()) {
      return this.name
    }

    return `(${this.capacityString()}) - ${this.name}`
  }

  @CallableOnce()
  setObjectRooms(objectRooms: ObjectRoom[]): void {
    this._objectRooms = objectRooms
  }

  get objectRooms(): ObjectRoom[] {
    return this._objectRooms
  }

  get objectRoomIds(): ObjectRoomId[] {
    return this._objectRooms.map((room: ObjectRoom) => room.objectRoomId)
  }
}
